import Button from '@atoms/Button/Button';
import { IAccordionItem } from '@models/IAccordionItem';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import AccordionItem from '@molecules/AccordionItem/AccordionItem';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { useState } from 'react';

interface Props {
  readonly accordionOpen: boolean;
  readonly anchorId: string;
  readonly background?: boolean;
  readonly cta: ILink;
  readonly items: IAccordionItem[];
  readonly meta: IMeta;
  readonly title: string;
}

const Accordion = ({
  accordionOpen,
  anchorId = '',
  background = false,
  cta,
  items,
  meta,
  title,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(accordionOpen ? 0 : -1);

  return (
    <section
      data-epi-block-id={meta?.contentLink}
      id={`a-${anchorId}`}
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-28-Accordion"
    >
      <div
        className={classNames(
          {
            'bg-grey-100 dark:bg-grey-600 xl:bg-transparent dark:xl:bg-transparent':
              background,
          },
          'container two-col:mx-0 two-col:px-0'
        )}
      >
        <div
          className={classNames(
            {
              'bg-grey-100 py-13 dark:bg-grey-600 lg:py-24': background,
            },
            'two-col:block two-col:py-0 two-col:lg:py-0 xl:grid xl:grid-cols-12'
          )}
        >
          <div className="xl:col-span-10 xl:col-start-2">
            {(title || (cta?.name && cta?.url)) && (
              <div className="mb-5 flex items-center justify-between two-col:px-0 lg:mb-10">
                {title && (
                  <h2
                    data-epi-property-name={meta?.editHints?.title}
                    className="h3"
                    data-testid="CT-28-Accordion-Title"
                  >
                    {title}
                  </h2>
                )}

                {cta?.name && cta?.url && (
                  <Button
                    linkCallback={() =>
                      updateDataLayer({
                        event: 'faq',
                        content_type: 'opened',
                        link_text: cta?.name,
                      })
                    }
                    link={cta}
                    editHint={meta?.editHints?.cta as string | undefined}
                    dataTestId="CT-28-Accordion-CTA"
                  />
                )}
              </div>
            )}

            <div
              data-epi-property-name={meta?.editHints?.items}
              className="grid w-full two-col:block lg:grid-cols-2 lg:gap-x-4"
            >
              {items.length > 0 &&
                items.map(
                  (
                    { title: itemTitle, text, meta: itemMeta }: IAccordionItem,
                    index
                  ) => (
                    <AccordionItem
                      background={background}
                      key={`${itemTitle}-${text}`}
                      activeIndex={activeIndex}
                      index={index}
                      isActive={index === activeIndex}
                      meta={itemMeta}
                      setActiveIndex={setActiveIndex}
                      text={text}
                      title={itemTitle}
                      dataTestId={`CT-28-Accordion-Item-${index}`}
                    />
                  )
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Accordion;
